/**
 * Every destination that hangs off a bhajan.
 *
 * A bhajan page is the lyrics. Several other things belong to a bhajan without
 * belonging on that page — its meaning is a page of prose to read, its western
 * notation is a score to play from, distraction-free is a way to sing from it —
 * and each of them wants its own URL, its own scroll position and its own print
 * pagination. This is the one list of them.
 *
 * One list, because they were previously designed one at a time: the score got a
 * full-width banner card, distraction-free got an unlabelled 16px glyph in the
 * page header, and the meaning got a section bolted to the bottom of the lyric
 * sheet. Three shapes for one idea, and no room for a fourth.
 *
 * Adding a destination is: a member of CompanionKind, a row in COMPANIONS, a
 * predicate in AVAILABLE, a glyph in COMPANION_GLYPHS, and a route. The first
 * four are total Records over the union, so a missing one fails to compile
 * rather than shipping a blank chip.
 */

export type CompanionKind = 'meaning' | 'westernNotes' | 'focus';

export interface CompanionDef {
  kind: CompanionKind;
  /**
   * Chip text. One or two words, and NO subtitle — the grey second line is most
   * of what made the banner this replaces read as an advertisement, and six of
   * them would be a wall. CompanionEntry has no blurb field on purpose.
   */
  label: string;
  /** Eyebrow above the title on the destination page. */
  eyebrow: string;
  /**
   * Accessible name. MUST contain `label` verbatim so WCAG 2.5.3 (Label in
   * Name) holds and voice control works — someone saying "click Meaning" has to
   * hit the thing that says Meaning. A test asserts this for every kind.
   */
  ariaLabel: (subject: string) => string;
  href: (nodeId: number | string) => string;
}

/**
 * ORDER IS RENDER ORDER, for every bhajan at every width. Content destinations
 * first, reading modes last, and never re-sorted by what happens to be
 * available: a stable position per label is what makes the row scannable at a
 * glance instead of something to re-read on each bhajan.
 */
export const COMPANIONS: readonly CompanionDef[] = [
  {
    kind: 'meaning',
    label: 'Meaning',
    eyebrow: 'Meaning',
    ariaLabel: (s) => `Meaning of ${s}`,
    href: (id) => `/nodes/${id}/meaning`,
  },
  {
    kind: 'westernNotes',
    label: 'Western Notes',
    eyebrow: 'Western Notes',
    ariaLabel: (s) => `Western Notes for ${s}`,
    href: (id) => `/nodes/${id}/western-notes`,
  },
  {
    // Visible label and accessible name share the "Distraction-free" prefix
    // deliberately. Labelling this "Focus mode" while naming it
    // "Distraction-free" would break 2.5.3 — the two must overlap.
    kind: 'focus',
    label: 'Distraction-free',
    eyebrow: 'Distraction-free',
    ariaLabel: (s) => `Distraction-free reading of ${s}`,
    href: (id) => `/distraction/nodes/${id}`,
  },
] as const;

export interface CompanionInput {
  /** Route param — a string or a number, both interpolate the same way. */
  nodeId: number | string;
  /** The bhajan's `meaning` edge; null when it has none, and while loading. */
  meaningNodeId: number | null;
  /** The bhajan's `westernNotes` edge; null when it has none, and while loading. */
  westernNotesNodeId: number | null;
  /**
   * APP.features.distraction. The one tenant-gated member here, and it is also
   * route-gated — with the flag off the route is not registered, so a chip
   * pointing at it would be a link to nothing. Everything else is EDGE-gated in
   * every tenant, per the rule stated twice in src/app/types.ts: per-bhajan
   * content shows wherever the bhajan does.
   */
  distractionEnabled: boolean;
  /** Bhajan display title in the reader's language; null while resolving. */
  subject: string | null;
}

const AVAILABLE: Record<CompanionKind, (i: CompanionInput) => boolean> = {
  meaning: (i) => i.meaningNodeId != null,
  westernNotes: (i) => i.westernNotesNodeId != null,
  focus: (i) => i.distractionEnabled,
};

export interface CompanionEntry {
  kind: CompanionKind;
  label: string;
  to: string;
  /** undefined while the title is unknown — the visible label is the name then. */
  ariaLabel: string | undefined;
}

/** Which destinations this bhajan actually has, in registry order. */
export function bhajanCompanions(input: CompanionInput): CompanionEntry[] {
  return COMPANIONS.filter((d) => AVAILABLE[d.kind](input)).map((d) => ({
    kind: d.kind,
    label: d.label,
    to: d.href(input.nodeId),
    ariaLabel: input.subject ? d.ariaLabel(input.subject) : undefined,
  }));
}

export function companionDef(kind: CompanionKind): CompanionDef {
  // Non-null: COMPANIONS is total over the union, and a test asserts it.
  return COMPANIONS.find((d) => d.kind === kind)!;
}
